import { Guid } from 'js-guid'
import {
  DEFAULT_INSTANCE_VISIBLE_DAYS,
  INSTRUMENT_RECURRING_SCHEME_MAP,
} from 'utils/constants'

export const IN_PROGRESS_INSTRUMENT_KEY = 'inProgressInstrument'

export const getInstanceVisibleDays = (instanceGroup) =>
  instanceGroup?.visible_days ?? DEFAULT_INSTANCE_VISIBLE_DAYS

/**
 * Persistent instruments may omit start_date after visit confirmation and are
 * still live until end_date. Visit-based non-persistent rows keep start_date
 * null until that visit is confirmed; those placeholders must stay hidden.
 */
export const canTreatNullStartDateAsLive = (schedule, scheduleId) => {
  const resolved = resolveSchedule(schedule, scheduleId)
  return (
    resolved?.recurring?.scheme === INSTRUMENT_RECURRING_SCHEME_MAP.persistent
  )
}

/**
 * Whether an instance is within its visible window at currentDate.
 * Preserves visible_days for normal instances; uses end_date when visible_days is 0
 * (never-expire / long-lived instances). Always respects end_date as a hard cap.
 */
export const isWithinInstanceVisibilityWindow = (
  instanceGroup,
  { start_date, end_date, schedule_id, scheduleId },
  currentDate,
) => {
  const schedule = resolveSchedule(
    instanceGroup?.metadata?.schedule,
    schedule_id || scheduleId,
  )

  if (!start_date) {
    if (!end_date || !canTreatNullStartDateAsLive(schedule)) {
      return false
    }

    return currentDate <= new Date(end_date)
  }

  const start = new Date(start_date)
  if (start > currentDate) return false

  if (end_date) {
    const end = new Date(end_date)
    if (currentDate > end) return false
  }

  const visibleDays = getInstanceVisibleDays(instanceGroup)
  const visibleUntil = new Date(start)
  visibleUntil.setDate(visibleUntil.getDate() + visibleDays)

  if (end_date && visibleDays === 0) {
    return currentDate <= new Date(end_date)
  }

  return currentDate <= visibleUntil
}

export const hasActiveInstanceInVisibilityWindow = (instanceGroup, currentDate) =>
  (instanceGroup?.instances || []).some(
    (instance) =>
      !instance.completion &&
      isWithinInstanceVisibilityWindow(instanceGroup, instance, currentDate),
  )

/** Includes completed instances — for persistent instruments that repeat. */
export const hasInstanceInVisibilityWindow = (instanceGroup, currentDate) =>
  (instanceGroup?.instances || []).some((instance) =>
    isWithinInstanceVisibilityWindow(instanceGroup, instance, currentDate),
  )
/** Snapshot of submitted answers per instance (same browser) for Review & Edit after completion */
export const COMPLETED_ENTRY_REVIEW_KEY = 'linktCompletedEntryReview'

const buildInProgressStorageKey = ({ participantId, instrumentId, instanceId }) => {
  const base = `${IN_PROGRESS_INSTRUMENT_KEY}_${participantId}_${instrumentId}`
  return instanceId ? `${base}_${instanceId}` : base
}

export const saveCompletedEntryForReview = ({
  participantId,
  instrumentId,
  instanceId,
  questions,
  lastUpdated,
}) => {
  if (!participantId || !instrumentId || !instanceId || !questions?.length)
    return
  try {
    localStorage.setItem(
      `${COMPLETED_ENTRY_REVIEW_KEY}_${participantId}_${instrumentId}_${instanceId}`,
      JSON.stringify({ questions, last_updated: lastUpdated }),
    )
  } catch (e) {
    // ignore quota / private mode
  }
}

/** Calendar pending/completed for instruments with no review payload (e.g. ARTICLE). */
export const saveLocalCompletionStamp = ({
  participantId,
  instrumentId,
  instanceId,
  lastUpdated,
}) => {
  if (!participantId || !instrumentId || !instanceId) return
  try {
    localStorage.setItem(
      `${COMPLETED_ENTRY_REVIEW_KEY}_${participantId}_${instrumentId}_${instanceId}`,
      JSON.stringify({ questions: [], last_updated: lastUpdated }),
    )
  } catch (e) {
    // ignore quota / private mode
  }
}

export const removeLocalCompletionStamp = ({
  participantId,
  instrumentId,
  instanceId,
}) => {
  if (!participantId || !instrumentId || !instanceId) return
  try {
    localStorage.removeItem(
      `${COMPLETED_ENTRY_REVIEW_KEY}_${participantId}_${instrumentId}_${instanceId}`,
    )
  } catch (e) {
    // ignore private mode
  }
}

export const getCompletedEntryForReview = ({
  participantId,
  instrumentId,
  instanceId,
}) => {
  if (!participantId || !instrumentId || !instanceId) return null
  try {
    const raw = localStorage.getItem(
      `${COMPLETED_ENTRY_REVIEW_KEY}_${participantId}_${instrumentId}_${instanceId}`,
    )
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

/** Local review snapshots keyed per instance (persistent instruments may not get API completion). */
export const getLocalCompletedInstancesForInstrument = (
  participantId,
  instrumentId,
) => {
  if (!participantId || !instrumentId || typeof localStorage === 'undefined') {
    return []
  }

  const prefix = `${COMPLETED_ENTRY_REVIEW_KEY}_${participantId}_${instrumentId}_`
  const localInstances = []

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key?.startsWith(prefix)) continue

    const instanceId = key.slice(prefix.length)
    try {
      const data = JSON.parse(localStorage.getItem(key))
      if (!data?.last_updated) continue

      localInstances.push({
        instance_id: instanceId,
        completion: data.last_updated,
        questions: data.questions,
      })
    } catch (e) {
      // ignore malformed entries
    }
  }

  return localInstances
}

/**
 * Merge browser-stored completion snapshots into instance groups so calendar
 * completed sections work for persistent instruments whose API instance keeps
 * completion null after submit.
 */
export const enrichInstancesWithLocalCompletions = (
  allInstances = [],
  participantId,
) => {
  if (!participantId || typeof localStorage === 'undefined') return allInstances

  return allInstances.map((instrument) => {
    const instrumentId =
      instrument?.instrument_id || instrument?.identity || instrument?.id
    if (!instrumentId) return instrument

    const localInstances = getLocalCompletedInstancesForInstrument(
      participantId,
      instrumentId,
    )
    if (!localInstances.length) return instrument

    const mergedInstances = (instrument.instances || []).map((instance) => ({
      ...instance,
    }))
    const isPersistent = isPersistentInstrumentGroup(instrument)

    localInstances.forEach((localInstance) => {
      const existing = mergedInstances.find(
        (instance) => instance.instance_id === localInstance.instance_id,
      )

      if (existing) {
        if (!existing.completion) {
          existing.completion = localInstance.completion
        }
        if (!existing.questions?.length && localInstance.questions?.length) {
          existing.questions = localInstance.questions
        }
        return
      }

      // Never-repeat instruments can submit under a generated instance_id while
      // the API row stays open. Stamp that open row so calendar pending drops.
      // GOLD-12455: never stamp a sibling schedule's open instance.
      if (!isPersistent) {
        const scheduleMeta = instrument?.metadata?.schedule || instrument?.schedule
        if (!isMultiScheduleMetadata(scheduleMeta)) {
          const openInstance = mergedInstances.find(
            (instance) => !instance.completion,
          )
          if (openInstance) {
            openInstance.completion = localInstance.completion
            if (
              !openInstance.questions?.length &&
              localInstance.questions?.length
            ) {
              openInstance.questions = localInstance.questions
            }
            return
          }
        }
      }

      mergedInstances.push(localInstance)
    })

    return { ...instrument, instances: mergedInstances }
  })
}

/**
 * Best-effort: questions + answers on completed instance from list/detail API (shape varies).
 */
export const extractCompletedInstanceQuestions = (completedInstrument) => {
  if (!completedInstrument) return []
  const inst = completedInstrument.instances?.find?.((i) => i?.completion)
  const asArray = (v) => (Array.isArray(v) && v.length ? v : null)

  if (inst) {
    const fromInst =
      asArray(inst.questions) ||
      asArray(inst.submission?.questions) ||
      asArray(inst.survey?.questions) ||
      asArray(inst.data?.questions) ||
      (inst.value &&
        typeof inst.value === 'object' &&
        asArray(inst.value.questions))
    if (fromInst) return fromInst
  }

  return asArray(completedInstrument.questions) || []
}

export const getInstrumentFromLocalStorage = ({
  instrumentId,
  participantId,
  instanceId,
}) => {
  if (!instrumentId || !participantId) return null

  if (instanceId) {
    const scopedRaw = localStorage.getItem(
      buildInProgressStorageKey({ participantId, instrumentId, instanceId }),
    )
    if (scopedRaw) return JSON.parse(scopedRaw)

    const legacyRaw = localStorage.getItem(
      buildInProgressStorageKey({ participantId, instrumentId }),
    )
    if (!legacyRaw) return null

    const legacyData = JSON.parse(legacyRaw)
    if (
      legacyData?.instance_id &&
      String(legacyData.instance_id) !== String(instanceId)
    ) {
      return null
    }
    return legacyData
  }

  const instrument = localStorage.getItem(
    buildInProgressStorageKey({ participantId, instrumentId }),
  )

  if (instrument) {
    return JSON.parse(instrument)
  }

  return null
}

const NON_ANSWER_QUESTION_TYPES = new Set([
  'introduction',
  'prompt',
  'submission',
  'review',
  'resume-review',
])

export const hasStoredSurveyAnswers = (storedInstrument) => {
  const questions = storedInstrument?.questions
  if (!Array.isArray(questions)) return false

  return questions.some(
    (question) =>
      !NON_ANSWER_QUESTION_TYPES.has(question?.type) &&
      Array.isArray(question?.answer) &&
      question.answer.length > 0,
  )
}

/**
 * Resolve the active instance for Save & Resume lookups on activity cards.
 */
export const resolveActiveInstanceIdForInstrument = (instrument) => {
  if (instrument?.instance_id) return instrument.instance_id

  const schedule = getInstrumentSchedule(instrument)
  const isPersistent =
    schedule?.recurring?.scheme === INSTRUMENT_RECURRING_SCHEME_MAP.persistent
  const instances = instrument?.instances || []
  const activeInstances = instances.filter((instance) => {
    if (!isPersistent && instance.completion) return false
    return isInstanceActiveInCurrentWindow(instance, schedule)
  })

  return pickEarliestInstanceByStartDate(activeInstances)?.instance_id || null
}

/**
 * Load in-progress draft for carousel/calendar cards.
 * Falls back to the legacy unscoped key for single-schedule instruments when
 * instance-specific lookup misses (common for array/object schedule payloads).
 */
export const getInProgressInstrumentForActivities = ({
  instrument,
  participantId,
  instanceId,
}) => {
  const instrumentId =
    instrument?.identity ||
    instrument?.instrument_id ||
    instrument?.id ||
    instrument?.metadata?.instrument_id

  if (!instrumentId || !participantId) return null

  const resolvedInstanceId =
    instanceId || resolveActiveInstanceIdForInstrument(instrument)

  const stored = getInstrumentFromLocalStorage({
    instrumentId,
    participantId,
    instanceId: resolvedInstanceId,
  })
  if (stored) return stored

  const scheduleMeta = instrument?.metadata?.schedule || instrument?.schedule
  if (isMultiScheduleMetadata(scheduleMeta)) return null

  return getInstrumentFromLocalStorage({
    instrumentId,
    participantId,
  })
}

export const saveInstrumentToLocalStorage = ({
  instrumentId,
  participantId,
  instrument,
}) => {
  if (!instrumentId || !participantId || !instrument) return

  const instanceId = resolveLocalStorageInstanceId(instrument)
  const storageKey = buildInProgressStorageKey({
    participantId,
    instrumentId,
    instanceId,
  })
  const now = new Date().toISOString()

  const existingData = getInstrumentFromLocalStorage({
    instrumentId,
    participantId,
    instanceId,
  })
  const existingQuestions =
    existingData?.questions && Array.isArray(existingData.questions)
      ? existingData.questions
      : []
  const incomingQuestions =
    instrument?.questions && Array.isArray(instrument.questions)
      ? instrument.questions
      : []

  // If we have existing stored data, always update it with latest answers from Redux (never replace and lose questions)
  if (existingData && existingQuestions.length > 0) {
    const byId = new Map()
    existingQuestions.forEach((q) => {
      if (q?.id) byId.set(q.id, { ...q })
    })
    // Apply latest answers from incoming (currently answered questions in Redux)
    incomingQuestions.forEach((newQ) => {
      if (!newQ?.id) return
      const existing = byId.get(newQ.id)
      const hasNewAnswer =
        newQ.answer !== undefined &&
        newQ.answer !== null &&
        (Array.isArray(newQ.answer)
          ? newQ.answer.length > 0
          : newQ.answer !== '')
      const merged = existing
        ? {
          ...existing,
          ...newQ,
          answer: hasNewAnswer ? newQ.answer : existing.answer,
          last_updated: now,
        }
        : { ...newQ, last_updated: now }
      byId.set(newQ.id, merged)
    })
    // Preserve order: existing order first, then any new questions not in existing
    const existingIds = new Set(existingQuestions.map((q) => q.id))
    const mergedQuestions = existingQuestions.map((q) => byId.get(q.id) || q)
    incomingQuestions.forEach((q) => {
      if (q?.id && !existingIds.has(q.id))
        mergedQuestions.push(byId.get(q.id) || q)
    })

    const mergedInstrument = {
      ...existingData,
      ...instrument,
      questions: mergedQuestions,
      last_updated: now,
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(mergedInstrument))
    } catch (e) {
      console.warn('Failed to save instrument progress', { error: e })
    }
    return
  }

  // No existing data: save instrument as-is
  const instrumentWithTimestamp = {
    ...instrument,
    last_updated: now,
    questions: incomingQuestions.map((q) => ({ ...q, last_updated: now })),
  }
  try {
    localStorage.setItem(storageKey, JSON.stringify(instrumentWithTimestamp))
  } catch (e) {
    console.warn('Failed to save instrument progress', { error: e })
  }
}

export const removeInstrumentFromLocalStorage = ({
  instrumentId,
  participantId,
  instanceId,
}) => {
  if (!instrumentId || !participantId) return

  if (instanceId) {
    localStorage.removeItem(
      buildInProgressStorageKey({ participantId, instrumentId, instanceId }),
    )
  }

  const legacyData = getInstrumentFromLocalStorage({ instrumentId, participantId })
  const shouldRemoveLegacy =
    !instanceId ||
    !legacyData?.instance_id ||
    String(legacyData.instance_id) === String(instanceId)

  if (shouldRemoveLegacy) {
    localStorage.removeItem(
      buildInProgressStorageKey({ participantId, instrumentId }),
    )
  }
}

/**
 * Build a Set of "instrumentId:instanceId" for all completed instances.
 * Used to avoid showing "In Progress" for instruments that are already completed.
 * @param {Array} allInstances - array of instrument instances from API (each has instrument_id, instances[])
 * @returns {Set<string>} - Set of "instrumentId:instanceId" keys
 */
export const getCompletedInstanceKeys = (allInstances = []) => {
  const keys = new Set()
  allInstances.forEach((item) => {
    const instrumentId = item.instrument_id ?? item.identity ?? item.id
    if (!instrumentId || !item.instances) return
    item.instances.forEach((inst) => {
      if (inst.completion && inst.instance_id) {
        keys.add(`${instrumentId}:${inst.instance_id}`)
      }
    })
  })
  return keys
}

/** True when metadata.schedule is a multi-schedule array (2+ entries). */
export const isMultiScheduleMetadata = (schedule) =>
  Array.isArray(schedule) && getSchedulesList(schedule).length > 1

const resolveLocalStorageInstanceId = (instrument) => {
  const schedule = instrument?.metadata?.schedule || instrument?.schedule
  if (!isMultiScheduleMetadata(schedule)) return undefined
  return (
    instrument?.instance_id ||
    pickEarliestInstanceByStartDate(instrument?.instances)?.instance_id
  )
}

/** Normalize schedule metadata to an array of schedule objects. */
export const getSchedulesList = (schedule) => {
  if (!schedule) return []
  if (Array.isArray(schedule)) {
    return schedule.filter((item) => item && typeof item === 'object')
  }
  if (typeof schedule === 'object') return [schedule]
  return []
}

/**
 * Resolve a single schedule object for display / expiration / platform checks.
 * Legacy: schedule object returned as-is.
 * Multi-schedule: match by scheduleId / schedule_id when provided; otherwise first item.
 */
export const resolveSchedule = (schedule, scheduleId) => {
  const schedules = getSchedulesList(schedule)
  if (!schedules.length) return {}
  if (!Array.isArray(schedule)) return schedules[0]
  if (scheduleId) {
    const matched = schedules.find(
      (item) =>
        String(item.scheduleId || item.schedule_id || '') === String(scheduleId),
    )
    if (matched) return matched
  }
  return schedules[0]
}

export const getScheduleVisibilityConfig = (schedule) => schedule?.visibility

/**
 * Resolve visibility config for an instrument/instance.
 * Multi-schedule: visibility lives on the matched schedule entry only.
 * Legacy: top-level metadata.visibility / visibilityRanges.
 */
export const resolveInstrumentVisibilityConfig = (instrument, instance) => {
  if (!instrument) return undefined

  const metadata = instrument.metadata || {}
  const scheduleMeta = metadata.schedule
  const scheduleId =
    instance?.schedule_id ||
    instance?.scheduleId ||
    instrument.schedule_id ||
    instrument.scheduleId ||
    instrument.instances?.[0]?.schedule_id ||
    instrument.instances?.[0]?.scheduleId

  if (isMultiScheduleMetadata(scheduleMeta)) {
    const schedule = resolveSchedule(scheduleMeta, scheduleId)
    return getScheduleVisibilityConfig(schedule)
  }

  if (instrument.visibility) {
    return instrument.visibility
  }

  const singleSchedule =
    instrument.schedule ||
    (scheduleMeta && !Array.isArray(scheduleMeta) ? scheduleMeta : null)

  if (scheduleId && singleSchedule) {
    const scheduleVisibility = getScheduleVisibilityConfig(singleSchedule)
    if (scheduleVisibility) return scheduleVisibility
  }

  const { visibility, visibilityRanges } = metadata

  if (visibility && typeof visibility === 'object' && visibility.visibility) {
    return visibility
  }

  if (typeof visibility === 'string') {
    return {
      visibility,
      visibility_ranges: visibilityRanges || [],
    }
  }

  return undefined
}

export const usesScheduleScopedVisibility = (instrument) => {
  const scheduleMeta = instrument?.metadata?.schedule
  if (isMultiScheduleMetadata(scheduleMeta)) return true
  if (instrument?.schedule_id) return true
  if (instrument?.visibility) return true

  const singleSchedule =
    scheduleMeta && !Array.isArray(scheduleMeta) ? scheduleMeta : null
  return !!(
    instrument?.instances?.[0]?.schedule_id &&
    getScheduleVisibilityConfig(singleSchedule)
  )
}

export const getInstrumentSchedule = (instrument, scheduleId) => {
  const schedule = instrument?.schedule || instrument?.metadata?.schedule
  return resolveSchedule(schedule, scheduleId)
}

/**
 * Whether Save & Resume is enabled for this instrument's schedule.
 * Supports legacy schedule objects and multi-schedule arrays via resolveSchedule.
 */
export const isPauseResumeSupportedForInstrument = (instrument) => {
  if (!instrument) return false

  const scheduleId =
    instrument?.schedule_id ||
    instrument?.instances?.[0]?.schedule_id ||
    instrument?.instances?.[0]?.scheduleId

  const schedule = getInstrumentSchedule(instrument, scheduleId)
  return !!schedule?.timeout_window?.draft
}

/** Web portal: keep instruments whose schedule is not mobile-only (shared by list + calendar). */
export const isNonMobileSchedulePlatform = (item) => {
  const schedule = item?.schedule || item?.metadata?.schedule
  const schedules = getSchedulesList(schedule)
  if (!schedules.length) return true

  const instances = item?.instances
  if (Array.isArray(instances) && instances.length && Array.isArray(schedule)) {
    return instances.some((inst) => {
      const sid = inst.schedule_id || inst.scheduleId
      const resolved = resolveSchedule(schedule, sid)
      return String(resolved?.platform_type || '').toLowerCase() !== 'mobile'
    })
  }

  const scheduleId =
    item?.schedule_id ||
    item?.scheduleId ||
    instances?.[0]?.schedule_id ||
    instances?.[0]?.scheduleId
  const resolved = resolveSchedule(schedule, scheduleId)
  const platformType = resolved?.platform_type
  return String(platformType || '').toLowerCase() !== 'mobile'
}

/**
 * Expand multi-schedule instruments into one display entry per active instance.
 * Legacy instruments (schedule object) stay as a single entry.
 */
export const expandInstrumentsByInstances = (instruments = []) => {
  const expanded = []

  instruments.forEach((instrument) => {
    const schedule = instrument?.metadata?.schedule
    const instances = instrument?.instances || []

    if (!isMultiScheduleMetadata(schedule)) {
      expanded.push(instrument)
      return
    }

    if (!instances.length) {
      // No active instances: keep one entry with first matched schedule for metadata safety
      expanded.push({
        ...instrument,
        metadata: {
          ...instrument.metadata,
          schedule: resolveSchedule(schedule),
        },
        schedule_id:
          resolveSchedule(schedule)?.scheduleId ||
          resolveSchedule(schedule)?.schedule_id,
        visibility: resolveSchedule(schedule)?.visibility,
      })
      return
    }

    instances.forEach((instance) => {
      const scheduleId = instance.schedule_id || instance.scheduleId
      const resolvedSchedule = resolveSchedule(schedule, scheduleId)
      expanded.push({
        ...instrument,
        metadata: {
          ...instrument.metadata,
          schedule: resolvedSchedule,
        },
        instances: [instance],
        schedule_id: scheduleId || resolvedSchedule?.scheduleId || resolvedSchedule?.schedule_id,
        visibility: resolvedSchedule?.visibility,
        display_key: `${instrument.instrument_id}:${scheduleId || 'default'}:${instance.instance_id}`,
      })
    })
  })

  return expanded
}

/** Pair each instance with its resolved schedule (multi-schedule aware). */
export const getScheduleInstanceEntries = (instrument) => {
  const schedule = instrument?.metadata?.schedule
  const instances = instrument?.instances || []
  const schedules = getSchedulesList(schedule)

  if (!isMultiScheduleMetadata(schedule)) {
    return [
      {
        schedule: schedules[0] || {},
        instances,
        instance: instances[0],
      },
    ]
  }

  if (!instances.length) {
    return schedules.map((item) => ({
      schedule: item,
      instances: [],
      instance: undefined,
    }))
  }

  return instances.map((instance) => ({
    schedule: resolveSchedule(
      schedule,
      instance.schedule_id || instance.scheduleId,
    ),
    instances: [instance],
    instance,
  }))
}

export const PERSISTENT_INSTRUMENT_SUBMITTED_KEY =
  'linktPersistentInstrumentSubmitted'

export const getInstrumentInstanceGroup = (
  instanceLists,
  instrumentId,
) => {
  if (!instrumentId) return null

  for (const list of instanceLists || []) {
    const found = (list || []).find(
      (item) =>
        item?.instrument_id === instrumentId ||
        item?.identity === instrumentId ||
        item?.id === instrumentId,
    )
    if (found) return found
  }

  return null
}

export const isPersistentInstrumentGroup = (group) => {
  const scheduleId =
    group?.schedule_id ||
    group?.instances?.[0]?.schedule_id ||
    group?.instances?.[0]?.scheduleId
  const schedule = resolveSchedule(
    group?.metadata?.schedule || group?.schedule,
    scheduleId,
  )
  return (
    schedule?.recurring?.scheme === INSTRUMENT_RECURRING_SCHEME_MAP.persistent
  )
}

export const isScheduleVisitDeployedOrPersistent = (schedule) =>
  canTreatNullStartDateAsLive(schedule) ||
  Boolean(schedule?.chain_deployment_info?.chain_deployment_by_ss_visit)

export const isInstanceActiveInCurrentWindow = (instance, schedule) => {
  if (instance?.completion) return false

  const currentDate = new Date()
  const resolved = resolveSchedule(
    schedule,
    instance?.schedule_id || instance?.scheduleId,
  )

  if (instance?.start_date) {
    const start = new Date(instance.start_date)
    if (start > currentDate) return false
  } else if (!instance?.end_date || !canTreatNullStartDateAsLive(resolved)) {
    return false
  }

  if (instance?.end_date) {
    return currentDate <= new Date(instance.end_date)
  }

  return true
}

/**
 * API instance arrays are not guaranteed to be chronological. When several
 * instances already match the same rule (e.g. all in today's window), pick the
 * earliest start_date. This is a tie-breaker only — not "always day 1 of series"
 * and not a substitute for calendar day selection (see resolveInstanceIdForCalendarDay).
 */
export const pickEarliestInstanceByStartDate = (instances = []) => {
  if (!instances?.length) return null

  const withStartDate = instances.filter((instance) => instance?.start_date)
  if (!withStartDate.length) return instances[0] || null

  return withStartDate.reduce((earliest, instance) => {
    if (!earliest) return instance
    return new Date(instance.start_date) < new Date(earliest.start_date)
      ? instance
      : earliest
  }, null)
}

/**
 * Active instance: not completed and current date within window.
 * Persistent instances may have null start_date after visit confirmation.
 * Visit-based non-persistent instances are live only once start_date is set.
 */
export const findActiveInstanceId = (instancesList, instrumentId) => {
  if (!instancesList?.length) return null

  const instrumentInstance = getInstrumentInstanceGroup(
    [instancesList],
    instrumentId,
  )

  if (!instrumentInstance?.instances) return null

  const schedule = instrumentInstance?.metadata?.schedule
  const currentDate = new Date()

  const activeInstances = instrumentInstance.instances.filter((instance) => {
    if (instance.completion) return false

    if (!instance.start_date) {
      return isInstanceActiveInCurrentWindow(instance, schedule)
    }

    const start = new Date(instance.start_date)
    const end = instance.end_date ? new Date(instance.end_date) : null

    if (start > currentDate) return false
    if (end && currentDate > end) return false

    return true
  })

  return pickEarliestInstanceByStartDate(activeInstances)?.instance_id || null
}

export const markPersistentInstrumentSubmitted = ({
  participantId,
  instrumentId,
}) => {
  if (!participantId || !instrumentId || typeof localStorage === 'undefined') {
    return
  }

  try {
    localStorage.setItem(
      `${PERSISTENT_INSTRUMENT_SUBMITTED_KEY}_${participantId}_${instrumentId}`,
      'true',
    )
  } catch (e) {
    // ignore quota / private mode
  }
}

export const hasPriorPersistentSubmission = ({
  instrumentId,
  participantId,
  allInstancesList = [],
  instancesList = [],
}) => {
  const group = getInstrumentInstanceGroup(
    [allInstancesList, instancesList],
    instrumentId,
  )

  if (group?.instances?.some((inst) => !!inst.completion)) {
    return true
  }

  if (participantId && instrumentId && typeof localStorage !== 'undefined') {
    if (
      localStorage.getItem(
        `${PERSISTENT_INSTRUMENT_SUBMITTED_KEY}_${participantId}_${instrumentId}`,
      )
    ) {
      return true
    }

    const reviewPrefix = `${COMPLETED_ENTRY_REVIEW_KEY}_${participantId}_${instrumentId}_`
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(reviewPrefix)) return true
    }
  }

  return false
}

const findOpenApiInstanceInGroup = (group, instanceId) => {
  if (!instanceId || !group?.instances?.length) return null

  const match = group.instances.find(
    (instance) => String(instance.instance_id) === String(instanceId),
  )

  if (match && !match.completion) {
    return match.instance_id
  }

  return null
}

/**
 * Resolve instance_id for survey open/submit.
 * Single-row persistent: after the first submission, later completions use a
 * new client instance_id. Multi-occurrence persistent (one API row per day):
 * each open occurrence keeps its API instance_id until completed (GOLD-12487).
 */
export const resolveInstanceIdForInstrument = ({
  instrumentId,
  instancesList = [],
  allInstancesList = [],
  participantId,
  existingInstanceId,
  inProgressDraft,
}) => {
  const group = getInstrumentInstanceGroup(
    [allInstancesList, instancesList],
    instrumentId,
  )
  const isPersistent = isPersistentInstrumentGroup(group)

  if (
    inProgressDraft?.instance_id &&
    inProgressDraft.state !== 'COMPLETE'
  ) {
    return inProgressDraft.instance_id
  }

  if (!isPersistent) {
    if (existingInstanceId) return existingInstanceId

    return (
      findActiveInstanceId(instancesList, instrumentId) ||
      findActiveInstanceId(allInstancesList, instrumentId) ||
      Guid.newGuid().StringGuid
    )
  }

  const openApiInstanceId = findOpenApiInstanceInGroup(group, existingInstanceId)
  if (openApiInstanceId) {
    return openApiInstanceId
  }

  if (
    hasPriorPersistentSubmission({
      instrumentId,
      participantId,
      allInstancesList,
      instancesList,
    })
  ) {
    return Guid.newGuid().StringGuid
  }

  return (
    findActiveInstanceId(instancesList, instrumentId) ||
    findActiveInstanceId(allInstancesList, instrumentId) ||
    existingInstanceId ||
    Guid.newGuid().StringGuid
  )
}

export const getInstrumentIdentity = (instrument = {}) =>
  instrument.instrument_id ||
  instrument.identity ||
  instrument.id ||
  instrument.metadata?.instrument_id ||
  null

/** Copy catalog `url` (json vs zip) onto instance groups that do not have one. */
export const attachCatalogUrls = (instruments = [], catalog = []) => {
  const urlById = new Map()
  catalog.forEach((item) => {
    const id = getInstrumentIdentity(item)
    if (!id || !item?.url) return
    urlById.set(String(id), item.url)
  })

  return instruments.map((instrument) => {
    if (!instrument || instrument.url) return instrument
    const id = getInstrumentIdentity(instrument)
    const url = id ? urlById.get(String(id)) : undefined
    return url ? { ...instrument, url } : instrument
  })
}

export const mergeInstances = (arr1 = [], arr2 = []) => {
  const mergedMap = new Map()

  ;[...arr1, ...arr2].forEach((instrument) => {
    const { instrument_id, instances = [] } = instrument || {}
    if (!instrument_id) return

    if (!mergedMap.has(instrument_id)) {
      mergedMap.set(instrument_id, {
        ...instrument,
        instances: instances.map((instance) => ({ ...instance })),
      })
      return
    }

    const existing = mergedMap.get(instrument_id)
    instances.forEach((incoming) => {
      const idx = existing.instances.findIndex(
        (item) => item.instance_id === incoming.instance_id,
      )
      if (idx === -1) {
        existing.instances.push({ ...incoming })
        return
      }

      const current = existing.instances[idx]
      existing.instances[idx] = {
        ...current,
        ...incoming,
        completion: current.completion || incoming.completion,
      }
    })
  })

  return Array.from(mergedMap.values())
}

/**
 * Check if a completed instrument can be edited based on metadata settings and time window
 * @param {Object} completedInstrument - The completed instrument object
 * @returns {boolean} - Whether the instrument can be edited
 */
export const canEditCompletedInstrument = (completedInstrument) => {
  if (!completedInstrument) return false

  const metadata = completedInstrument.metadata || {}

  // Find the completed instance
  const completedInstance = completedInstrument?.instances?.find(
    (instance) => !!instance.completion,
  )

  if (!completedInstance || !completedInstance.completion) return false

  // Check for edit timeout window in the schedule (primary structure)
  // Structure: metadata.schedule.timeout_window.edit.value
  const schedule = resolveSchedule(
    metadata.schedule,
    completedInstance.schedule_id || completedInstance.scheduleId,
  )
  const timeoutWindow = schedule.timeout_window || {}
  const editWindow = timeoutWindow.edit?.value

  // If edit window is defined in schedule.timeout_window.edit.value, use it
  if (editWindow !== undefined && editWindow !== null) {
    const completionTime = new Date(completedInstance.completion)
    const now = new Date()
    const timeDiffMs = now - completionTime
    const timeDiffMinutes = timeDiffMs / (1000 * 60)

    // editWindow is in minutes
    if (timeDiffMinutes > editWindow) {
      return false
    }
    // If we're within the window, editing is allowed
    return true
  }

  // Fallback: Check if edit after completion is enabled (legacy or alternative structures)
  // Support multiple possible field name variations and nested structures
  const editAfterCompletion =
    metadata.edit_after_completion ||
    metadata.editAfterCompletion ||
    metadata.allow_edit_after_completion ||
    metadata.allowEditAfterCompletion ||
    metadata.can_edit_after_completion ||
    metadata.canEditAfterCompletion ||
    metadata.edit?.enabled ||
    metadata.edit?.after_completion ||
    metadata.settings?.edit_after_completion ||
    metadata.settings?.editAfterCompletion ||
    // Check if any metadata key contains 'edit' and is truthy
    Object.keys(metadata).some(
      (key) =>
        key.toLowerCase().includes('edit') &&
        (metadata[key] === true || metadata[key] === 'true'),
    )

  if (!editAfterCompletion) return false

  // Check if within time window (if specified) - legacy field names
  const legacyEditWindow =
    metadata.edit_after_completion_window ||
    metadata.editAfterCompletionWindow ||
    metadata.edit_window ||
    metadata.editWindow ||
    metadata.edit_time_window ||
    metadata.editTimeWindow ||
    metadata.edit?.window ||
    metadata.edit?.time_window ||
    metadata.settings?.edit_window ||
    metadata.settings?.editWindow

  if (legacyEditWindow) {
    const completionTime = new Date(completedInstance.completion)
    const now = new Date()
    const timeDiffMs = now - completionTime
    const timeDiffMinutes = timeDiffMs / (1000 * 60)

    // editWindow is assumed to be in minutes
    if (timeDiffMinutes > legacyEditWindow) {
      return false
    }
  }

  return true
}

/**
 * Whether in-progress data in localStorage may still be resumed for this instrument.
 * Mirrors canEditCompletedInstrument: uses schedule.timeout_window.draft.value (minutes),
 * measured from stored last_updated (not completion time).
 * @param {Object} instrument - List/API instrument with schedule or metadata.schedule
 * @param {Object|null|undefined} storedInstrument - from getInstrumentFromLocalStorage
 * @returns {boolean}
 */
export const canResumeInProgressInstrument = (instrument, storedInstrument) => {
  if (!storedInstrument) return false

  const lastUpdated = storedInstrument.last_updated
  if (!lastUpdated) return false
  const last = new Date(lastUpdated)
  if (Number.isNaN(last.getTime())) return false
  const now = new Date()
  const timeDiffMinutes = (now - last) / (1000 * 60)

  const scheduleId =
    storedInstrument.schedule_id ||
    storedInstrument.scheduleId ||
    instrument?.schedule_id ||
    instrument?.instances?.[0]?.schedule_id ||
    instrument?.instances?.[0]?.scheduleId

  const instrumentDraft = getInstrumentSchedule(
    instrument,
    scheduleId,
  )?.timeout_window?.draft?.value
  const storedDraft = getInstrumentSchedule(
    storedInstrument,
    scheduleId,
  )?.timeout_window?.draft?.value
  const draftWindow =
    instrumentDraft !== undefined && instrumentDraft !== null
      ? instrumentDraft
      : storedDraft

  if (draftWindow !== undefined && draftWindow !== null) {
    if (timeDiffMinutes > draftWindow) return false
    return true
  }

  // Activities payloads may omit timeout_window.draft even when save/resume is enabled.
  return hasStoredSurveyAnswers(storedInstrument)
}
